import React, { useEffect, useState } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import Icon from '@common/assets';
import useProfileBasic from '@repository/profiles/useProfileBasic';
import useProfileConditions from '@repository/profiles/useProfileConditions';
import useProfileEducation from '@repository/profiles/useProfileEducation';
import useProfileCareer from '@repository/profiles/useProfileCareer';
import useProfileSkill from '@repository/profiles/useProfileSkill';
import useProfileExperience from '@repository/profiles/useProfileExperience';
import useProfilePrize from '@repository/profiles/useProfilePrize';
import useProfilePreferential from '@repository/profiles/useProfilePreferential';
import useProfileFile from '@repository/profiles/useProfileFile';
import { useUserInfo } from '@repository/auth/useUserInfo';
import useProfileProjects from '@repository/profiles/useProfileProject';
import { useMatchProfile } from '@repository/match';
import Banner from '@components/profile/banner';
import { ProfileInfo, ProfileInfoType } from '@domain/constant/profile/ProfileOverlayType';
import Colors from '@domain/constant/colors';

import { MatchRouteUrl } from '@utils/routeUtil';
import classnames from 'classnames/bind';
import styles from './profileSnb.module.scss';

const cx = classnames.bind(styles);

interface IScrollLinkItemProps {
  toInfo: ProfileInfoType;
  activeTab: ProfileInfoType;
  onSetActive: (type: ProfileInfoType) => void;
  on: boolean;
  children: React.ReactNode;
}

const ScrollLinkItem = ({ toInfo, onSetActive, on, activeTab, children }: IScrollLinkItemProps) => {
  return (
    <ScrollLink
      to={toInfo}
      offset={-112}
      smooth={true}
      spy={true}
      className={cx('snbTab', { active: toInfo === activeTab, on })}
      onSetActive={() => onSetActive(toInfo)}
    >
      {children}
      <span className={cx('icon')}>
        <Icon name="checkLight" width={20} height={20} fill={Colors.C_WHITE} />
      </span>
    </ScrollLink>
  );
};

interface ProfileSnbProps {
  handleClickCareerBanner: () => void;
}

const ProfileSnb = ({ handleClickCareerBanner }: ProfileSnbProps) => {
  const [activeTab, setActiveTab] = useState<ProfileInfoType>(ProfileInfo.BASIC_INFO);
  const [progressPercentage, setProgressPercentage] = useState<number>(0);
  const [isSmallHeight, setIsSmallHeight] = useState<boolean>(false);

  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;

  const { data: matchProfile } = useMatchProfile();

  const { data: profileBasicData } = useProfileBasic(isLogin);
  const { data: profileConditionsData } = useProfileConditions(isLogin);
  const { data: profileEducationData } = useProfileEducation(isLogin);
  const { data: profileCareersData } = useProfileCareer(isLogin);
  const { data: profileProjectsData } = useProfileProjects(isLogin);
  const { data: profileSkillData } = useProfileSkill(isLogin);
  const { data: profileExperienceData } = useProfileExperience(isLogin);
  const { data: profilePrizeData } = useProfilePrize(isLogin);
  const { data: profilePreferentialData } = useProfilePreferential(isLogin);
  const { data: profileFileData } = useProfileFile(isLogin);

  const onScroll = () => {
    //스크롤 맨 하단 도달시 첨부파일 선택되도록 함
    if (document.documentElement.scrollTop + document.body.clientHeight === document.body.scrollHeight) {
      setActiveTab(ProfileInfo.ATTACHED_FILE);
    }

    if (document.documentElement.scrollTop <= 0) {
      setActiveTab(ProfileInfo.BASIC_INFO);
    }
  };

  useEffect(() => {
    document.addEventListener('scroll', onScroll);

    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, []);

  const onResize = () => {
    const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    if (height <= 1020) setIsSmallHeight(true);
    else setIsSmallHeight(false);
  };

  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    setProgressPercentage(matchProfile?.profile?.completionRate || 0);
  }, [matchProfile?.profile?.completionRate]);

  return (
    <div className={cx('snbArea')}>
      <Banner handleClickCareerBanner={handleClickCareerBanner} />

      <div className={cx('snbWrap')}>
        <div className={cx('snbTitle')}>프로필 항목</div>

        <div className={cx('descArea')}>
          필수정보 입력하면{' '}
          <a className={cx('link', 'pc_profile_snb_match_link_0831')} href={MatchRouteUrl.MATCH_INTRODUCE}>
            제안을 받을 수 있어요.
          </a>
        </div>

        <div className={cx('progressArea')}>
          <span className={cx('progressText')}>{progressPercentage}% 완성</span>
          <div className={cx('progressBarWrap')}>
            <span className={cx('progressBarBackGround')} />
            <span className={cx('progressBar')} style={{ width: `${progressPercentage}%` }} />
          </div>
        </div>

        <div className={cx('snbTabArea', { scroll: isSmallHeight })}>
          <ScrollLinkItem
            toInfo={ProfileInfo.BASIC_INFO}
            on={!!profileBasicData?.allSaveYn}
            activeTab={activeTab}
            onSetActive={setActiveTab}
          >
            <span className={cx('tabText', 'require')}>기본정보</span>
          </ScrollLinkItem>
          <ScrollLinkItem
            toInfo={ProfileInfo.PREFER}
            on={!!profileConditionsData?.allSaveYn}
            activeTab={activeTab}
            onSetActive={setActiveTab}
          >
            <span className={cx('tabText', 'require')}>선호정보</span>
          </ScrollLinkItem>
          <ScrollLinkItem
            toInfo={ProfileInfo.EDU}
            on={!!profileEducationData?.allSaveYn}
            activeTab={activeTab}
            onSetActive={setActiveTab}
          >
            <span className={cx('tabText', 'require')}>학력</span>
          </ScrollLinkItem>
          <ScrollLinkItem
            toInfo={ProfileInfo.CAREER}
            on={!!profileCareersData?.allSaveYn}
            activeTab={activeTab}
            onSetActive={setActiveTab}
          >
            <span className={cx('tabText')}>경력</span>
          </ScrollLinkItem>
          <ScrollLinkItem
            toInfo={ProfileInfo.PROJECT}
            on={!!profileProjectsData?.allSaveYn}
            activeTab={activeTab}
            onSetActive={setActiveTab}
          >
            <span className={cx('tabText')}>프로젝트</span>
          </ScrollLinkItem>
          <ScrollLinkItem
            toInfo={ProfileInfo.SKILL}
            on={!!profileSkillData?.allSaveYn}
            activeTab={activeTab}
            onSetActive={setActiveTab}
          >
            <span className={cx('tabText')}>지식 · 기술</span>
          </ScrollLinkItem>
          <ScrollLinkItem
            toInfo={ProfileInfo.EXPERIENCE}
            on={!!profileExperienceData?.allSaveYn}
            activeTab={activeTab}
            onSetActive={setActiveTab}
          >
            <span className={cx('tabText')}>경험</span>
          </ScrollLinkItem>
          <ScrollLinkItem
            toInfo={ProfileInfo.PRIZE}
            on={!!profilePrizeData?.allSaveYn}
            activeTab={activeTab}
            onSetActive={setActiveTab}
          >
            <span className={cx('tabText')}>수상내역</span>
          </ScrollLinkItem>
          <ScrollLinkItem
            toInfo={ProfileInfo.MILITARY_PREFERENCE}
            on={!!profilePreferentialData?.allSaveYn}
            activeTab={activeTab}
            onSetActive={setActiveTab}
          >
            <span className={cx('tabText')}>병역 · 취업 우대</span>
          </ScrollLinkItem>
          <ScrollLinkItem
            toInfo={ProfileInfo.ATTACHED_FILE}
            on={!!profileFileData?.allSaveYn}
            activeTab={activeTab}
            onSetActive={setActiveTab}
          >
            <span className={cx('tabText')}>첨부자료</span>
          </ScrollLinkItem>
        </div>
      </div>
    </div>
  );
};

export default ProfileSnb;
